import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Zap, Database, Phone, Mail, MessageSquare, Users, Settings, GitBranch, RefreshCw, CheckCircle, Activity, AlertTriangle } from 'lucide-react';
import Header from '../../components/ui/Header';
import ServiceStatusDashboard from './components/ServiceStatusDashboard';
import WorkflowCanvas from './components/WorkflowCanvas';
import EdgeFunctionsMonitor from './components/EdgeFunctionsMonitor';
import WebhookManager from './components/WebhookManager';
import DataMappingTools from './components/DataMappingTools';
import AutomationRules from './components/AutomationRules';
import { useToast } from '../../components/ui/Toast';

const SERVICES = [
  {
    id: 'ghl', name: 'GoHighLevel', description: 'CRM & Pipeline Management',
    icon: Globe, bgColor: 'bg-orange-100', iconColor: 'text-orange-600',
    status: 'connected', apiRate: '847/1000', syncPerf: '99.2%', lastSync: '2m ago'
  },
  {
    id: 'dialer', name: 'AI Dialer', description: 'Automated Call Sequences',
    icon: Phone, bgColor: 'bg-blue-100', iconColor: 'text-blue-600',
    status: 'connected', apiRate: '312/500', syncPerf: '98.7%', lastSync: '5m ago'
  },
  {
    id: 'resend', name: 'Resend Email', description: 'Transactional Email Service',
    icon: Mail, bgColor: 'bg-green-100', iconColor: 'text-green-600',
    status: 'connected', apiRate: '1.2k/5k', syncPerf: '99.8%', lastSync: '1m ago'
  },
  {
    id: 'twilio', name: 'Twilio SMS', description: 'SMS & Messaging Platform',
    icon: MessageSquare, bgColor: 'bg-red-100', iconColor: 'text-red-600',
    status: 'warning', apiRate: '489/500', syncPerf: '97.1%', lastSync: '3m ago',
    errorMessage: 'Approaching rate limit — consider upgrading plan'
  },
  {
    id: 'leads', name: 'Lead Sources', description: 'Multi-channel Lead Ingestion',
    icon: Users, bgColor: 'bg-purple-100', iconColor: 'text-purple-600',
    status: 'connected', apiRate: 'N/A', syncPerf: '100%', lastSync: 'Live'
  },
  {
    id: 'supabase', name: 'Supabase DB', description: 'Database & Edge Functions',
    icon: Database, bgColor: 'bg-teal-100', iconColor: 'text-teal-600',
    status: 'connected', apiRate: 'N/A', syncPerf: '100%', lastSync: 'Live'
  },
];

const WORKFLOWS = [
  {
    id: 'wf1', name: 'Lead Capture → CRM Sync', active: true, executions: 142, lastRun: '2 min ago', avgDuration: '1.2s', successRate: 99,
    nodes: [
      { label: 'New Lead', type: 'trigger', icon: Users },
      { label: 'Score Lead', type: 'action', icon: Zap },
      { label: 'Score > 70?', type: 'condition', icon: GitBranch },
      { label: 'Create GHL Contact', type: 'action', icon: Globe },
      { label: 'Send Welcome Email', type: 'output', icon: Mail },
    ]
  },
  {
    id: 'wf2', name: 'High-Score Lead → Dialer Queue', active: true, executions: 67, lastRun: '8 min ago', avgDuration: '0.8s', successRate: 97,
    nodes: [
      { label: 'Lead Score Updated', type: 'trigger', icon: Zap },
      { label: 'Score ≥ 85?', type: 'condition', icon: GitBranch },
      { label: 'Add to Dialer Queue', type: 'action', icon: Phone },
      { label: 'SMS Notification', type: 'output', icon: MessageSquare },
    ]
  },
  {
    id: 'wf3', name: 'Deal Won → Follow-up Sequence', active: false, executions: 23, lastRun: '1 hr ago', avgDuration: '2.1s', successRate: 100,
    nodes: [
      { label: 'Deal Won', type: 'trigger', icon: Globe },
      { label: 'Update DB Record', type: 'action', icon: Database },
      { label: 'Send Thank You Email', type: 'action', icon: Mail },
      { label: 'Schedule Follow-up', type: 'output', icon: Zap },
    ]
  },
];

const EDGE_FUNCTIONS = [
  { id: 'ef1', name: 'send-email', endpoint: '/functions/v1/send-email', invocations: '1,247', avgLatency: '234ms', errorRate: '0.1%', status: 'active' },
  { id: 'ef2', name: 'send-sms', endpoint: '/functions/v1/send-sms', invocations: '892', avgLatency: '189ms', errorRate: '0.3%', status: 'active' },
  { id: 'ef3', name: 'make-call', endpoint: '/functions/v1/make-call', invocations: '456', avgLatency: '312ms', errorRate: '1.2%', status: 'active' },
  { id: 'ef4', name: 'create-payment-intent', endpoint: '/functions/v1/create-payment-intent', invocations: '234', avgLatency: '445ms', errorRate: '0.0%', status: 'active' },
  { id: 'ef5', name: 'confirm-payment', endpoint: '/functions/v1/confirm-payment', invocations: '198', avgLatency: '389ms', errorRate: '0.5%', status: 'active' },
];

const TABS = [
  { id: 'status', label: 'Service Status', icon: Activity },
  { id: 'workflows', label: 'Workflows', icon: Zap },
  { id: 'edge', label: 'Edge Functions', icon: Database },
  { id: 'webhooks', label: 'Webhooks', icon: Globe },
  { id: 'mapping', label: 'Data Mapping', icon: GitBranch },
  { id: 'rules', label: 'Automation Rules', icon: Settings },
];

const CrossPlatformIntegrationHub = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('status');
  const [services, setServices] = useState(SERVICES);
  const [workflows, setWorkflows] = useState(WORKFLOWS);
  const [edgeFunctions] = useState(EDGE_FUNCTIONS);
  const [refreshing, setRefreshing] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [rules, setRules] = useState([]);
  const [logs] = useState([
    { id: 1, function: 'send-email', status: 'success', duration: '234ms', time: '2 min ago' },
    { id: 2, function: 'send-sms', status: 'success', duration: '189ms', time: '5 min ago' },
    { id: 3, function: 'make-call', status: 'error', duration: '312ms', time: '10 min ago', error: 'Twilio rate limit exceeded' },
    { id: 4, function: 'create-payment-intent', status: 'success', duration: '445ms', time: '15 min ago' },
    { id: 5, function: 'send-email', status: 'success', duration: '198ms', time: '20 min ago' },
  ]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await new Promise(r => setTimeout(r, 1000));
    setRefreshing(false);
    toast?.success('Services refreshed successfully!', 'Refreshed ✓');
  }, [toast]);

  const handleToggleWorkflow = useCallback((id) => {
    setWorkflows(prev => prev?.map(w => {
      if (w?.id !== id) return w;
      const next = { ...w, active: !w?.active };
      toast?.success(`Workflow "${w?.name}" ${next?.active ? 'activated' : 'paused'}.`, next?.active ? 'Activated' : 'Paused');
      return next;
    }));
  }, [toast]);

  const handleAddWebhook = useCallback((webhook) => {
    setWebhooks(prev => [...prev, { ...webhook, id: webhook?.id || Date.now() }]);
    toast?.success('Webhook added successfully!', 'Webhook Added ✓');
  }, [toast]);

  const handleDeleteWebhook = useCallback((id) => {
    setWebhooks(prev => prev?.filter(w => w?.id !== id));
    toast?.info('Webhook deleted.', 'Deleted');
  }, [toast]);

  const handleToggleWebhook = useCallback((id) => {
    setWebhooks(prev => prev?.map(w => w?.id === id ? { ...w, active: !w?.active } : w));
    toast?.success('Webhook status updated!', 'Updated');
  }, [toast]);

  const handleAddMapping = useCallback((mapping) => {
    setMappings(prev => [...prev, { ...mapping, id: mapping?.id || Date.now() }]);
    toast?.success('Mapping added successfully!', 'Mapping Added ✓');
  }, [toast]);

  const handleDeleteMapping = useCallback((id) => {
    setMappings(prev => prev?.filter(m => m?.id !== id));
    toast?.info('Mapping deleted.', 'Deleted');
  }, [toast]);

  const handleAddRule = useCallback((rule) => {
    setRules(prev => [...prev, { ...rule, id: rule?.id || Date.now() }]);
    toast?.success('Rule added successfully!', 'Rule Added ✓');
  }, [toast]);

  const handleDeleteRule = useCallback((id) => {
    setRules(prev => prev?.filter(r => r?.id !== id));
    toast?.info('Rule deleted.', 'Deleted');
  }, [toast]);

  const handleToggleRule = useCallback((id) => {
    setRules(prev => prev?.map(r => r?.id === id ? { ...r, active: !r?.active } : r));
    toast?.success('Rule status updated!', 'Updated');
  }, [toast]);

  const connectedCount = services?.filter(s => s?.status === 'connected')?.length;
  const warningCount = services?.filter(s => s?.status === 'warning')?.length;
  const activeWorkflows = workflows?.filter(w => w?.active)?.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cross-Platform Integration Hub</h1>
              <p className="text-gray-500 text-sm">Monitor and manage all your service integrations</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh All'}</span>
          </button>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Connected Services', value: connectedCount, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Warnings', value: warningCount, icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
            { label: 'Active Workflows', value: activeWorkflows, icon: Zap, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Edge Functions', value: edgeFunctions?.length, icon: Database, color: 'text-purple-600', bg: 'bg-purple-50' },
          ]?.map((stat, i) => {
            const StatIcon = stat?.icon;
            return (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{stat?.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat?.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${stat?.bg}`}>
                    <StatIcon className={`w-5 h-5 ${stat?.color}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="flex overflow-x-auto border-b border-gray-200">
            {TABS?.map(tab => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' :'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {activeTab === 'status' && (
              <ServiceStatusDashboard services={services} onRefresh={handleRefresh} />
            )}
            {activeTab === 'workflows' && (
              <WorkflowCanvas
                workflows={workflows}
                onToggleWorkflow={handleToggleWorkflow}
              />
            )}
            {activeTab === 'edge' && (
              <EdgeFunctionsMonitor functions={edgeFunctions} logs={logs} />
            )}
            {activeTab === 'webhooks' && (
              <WebhookManager
                webhooks={webhooks}
                onAdd={handleAddWebhook}
                onDelete={handleDeleteWebhook}
                onToggle={handleToggleWebhook}
              />
            )}
            {activeTab === 'mapping' && (
              <DataMappingTools
                mappings={mappings}
                onAdd={handleAddMapping}
                onDelete={handleDeleteMapping}
              />
            )}
            {activeTab === 'rules' && (
              <AutomationRules
                rules={rules}
                onAdd={handleAddRule}
                onDelete={handleDeleteRule}
                onToggle={handleToggleRule}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossPlatformIntegrationHub;
